import { createSlice } from "@reduxjs/toolkit";

import type { VehicleType } from "@busbud/int-schemas/lib/vehicles";
import type { Amenity } from "@busbud/int-schemas/udm";

import type {
  VehicleCategoryKey,
  VehicleLayoutTypeKey
} from "@app/modules/results/state/filters-types";

import type { CaseReducer, PayloadAction } from "@reduxjs/toolkit";

import { buildClientInitialResultsState } from "../utils/filters";

export type DepartureTimeRange = "nighttime" | "early" | "midday" | "late";

export interface FiltersState {
  amenities: Amenity[];
  departure_time_ranges: DepartureTimeRange[];
  // destination: number[];
  destination_location: number[];
  destination_poi: string[];
  excluded_sources: number[];
  has_e_ticket: string[];
  is_direct_only: string[];
  award_winners_only: string[];
  operator_ids: string[];
  profile_ids: number[];
  operator_names?: string[];
  // origin: number[];
  origin_location: number[];
  origin_poi: string[];
  vehicle_category: VehicleCategoryKey;
  vehicle_layout_type: VehicleLayoutTypeKey;
  vehicle_type: VehicleType[];
  classes: string[];
  max_price: number[];
}

const initial_state: FiltersState = buildClientInitialResultsState();

const setVehicleCategoryReducer: CaseReducer<
  FiltersState,
  PayloadAction<VehicleCategoryKey>
> = (state, action) => {
  state.vehicle_category = action.payload;
};

const setVehicleLayoutTypeReducer: CaseReducer<
  FiltersState,
  PayloadAction<VehicleLayoutTypeKey>
> = (state, action) => {
  state.vehicle_layout_type = action.payload;
};

const filters = createSlice({
  name: "filters",
  initialState: initial_state,
  reducers: {
    setVehicleCategory: setVehicleCategoryReducer,
    setVehicleLayoutType: setVehicleLayoutTypeReducer,
    resetFilters: _state => initial_state,
    removeExcludedSources: (
      state,
      _action: PayloadAction<
        | {
            meta?: {
              ignore_tracking: boolean;
            };
          }
        | undefined
      >
    ) => {
      state.excluded_sources = [];
    },
    setOperators: (state, { payload }: PayloadAction<string[]>) => {
      state.operator_ids = payload;
    },
    setOperatorProfiles: (state, { payload }: PayloadAction<number[]>) => {
      state.profile_ids = payload;
    },
    addDepartureTimeRange: (
      state,
      action: PayloadAction<DepartureTimeRange>
    ) => {
      state.departure_time_ranges.push(action.payload);
    },
    removeDepartureTimeRange: (
      state,
      action: PayloadAction<DepartureTimeRange>
    ) => {
      state.departure_time_ranges = state.departure_time_ranges.filter(
        item => item !== action.payload
      );
    },
    addHasETicket: (state, action: PayloadAction<string>) => {
      state.has_e_ticket.push(action.payload);
    },
    removeHasETicket: state => {
      state.has_e_ticket = [];
    },
    addIsDirectOnly: (state, action: PayloadAction<string>) => {
      state.is_direct_only.push(action.payload);
    },
    removeIsDirectOnly: state => {
      state.is_direct_only = [];
    },
    addAwardWinnersOnly: (state, action: PayloadAction<string>) => {
      state.award_winners_only.push(action.payload);
    },
    removeAwardWinnersOnly: state => {
      state.award_winners_only = [];
    },
    selectVehicleType: (state, action: PayloadAction<VehicleType[]>) => {
      state.vehicle_type = action.payload;
    },
    removeVehicleType: state => {
      state.vehicle_type = [];
    },
    // setOrigin: (state, { payload }: PayloadAction<number[]>) => {
    //   state.origin = payload;
    // },
    // setDestination: (state, { payload }: PayloadAction<number[]>) => {
    //   state.destination = payload;
    // },
    addClass: (state, action: PayloadAction<string>) => {
      state.classes.push(action.payload);
    },
    removeClass: (state, action: PayloadAction<string>) => {
      const index = state.classes.findIndex(item => item === action.payload);
      state.classes.splice(index, 1);
    },
    setClasses: (state, action: PayloadAction<string>) => {
      state.classes.push(action.payload);
    },
    setMaxPrice: (state, { payload }: PayloadAction<number>) => {
      state.max_price = [payload];
    },
    setOriginPoi: (state, { payload }: PayloadAction<PoiFiltersPayload>) => {
      state.origin_poi = Object.keys(payload);
    },
    setDestinationPoi: (
      state,
      { payload }: PayloadAction<PoiFiltersPayload>
    ) => {
      state.destination_poi = Object.keys(payload);
    }
  }
});

type PoiFiltersPayload = Record<string, string>;

export const {
  setVehicleCategory,
  setVehicleLayoutType,
  resetFilters,
  removeExcludedSources,
  setOperators,
  setOperatorProfiles,
  addDepartureTimeRange,
  removeDepartureTimeRange,
  addHasETicket,
  removeHasETicket,
  addIsDirectOnly,
  removeIsDirectOnly,
  addAwardWinnersOnly,
  removeAwardWinnersOnly,
  selectVehicleType,
  removeVehicleType,
  // setOrigin,
  // setDestination,
  addClass,
  removeClass,
  setMaxPrice,
  setOriginPoi,
  setDestinationPoi
} = filters.actions;

export default filters.reducer;
